import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import DateService from 'src/core/services/DateService';
import { useReactivateTreaty } from '@common/slices';
import { reactivateTreatyDrawerSections } from '@common/constants';
import { IReactivateTreatyValues, IBaseDrawerProps } from '@common/types';
import { getTreatyDetailsQuery } from '@modules/reinsurance/treaties/treaty-details-page/queries';
import { RootState } from '@core/redux/store';
import { useSelector } from 'react-redux';

interface ReactivateTreatyDrawerProps extends IBaseDrawerProps {
  treatyId: string;
}

const ReactivateTreatyDrawer: React.FC<ReactivateTreatyDrawerProps> = ({
  treatyId,
  onSuccess
}) => {
  const submitButtonState = useSelector(
    (state: RootState) => state.apiState.buttonState
  );

  const [values, setValues] = useState<IReactivateTreatyValues>({
    NewTreatyExpiryDate: null
  });

  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);
  const [reactivateTreaty] = useReactivateTreaty({
    refetchQueries: [getTreatyDetailsQuery],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    setSections(reactivateTreatyDrawerSections(values));
  }, [values]);

  const submitForm = async (formValues: Record<string, any>) => {
    try {
      const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

      const expiryDate = DateService.formatDateTimeBackend(
        data.NewTreatyExpiryDate
      );

      if (!expiryDate) {
        throw new Error('Invalid expiry date');
      }

      const res = await reactivateTreaty({
        treatyId,
        expiryDate
      });
      if (!res.errors) {
        onSuccess();
      }
    } catch (error) {
      console.error(error);
      toast.error(<ToastErrorMessage>{error.message}</ToastErrorMessage>);
    }
  };

  const handleChange = (
    fieldName: keyof IReactivateTreatyValues,
    value: any,
    sectionIndex: number,
    updatedValues: IReactivateTreatyValues
  ) => {
    setValues(updatedValues);
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
    />
  );
};

export { ReactivateTreatyDrawer };
